const STRIPE_PAYMENT_METHOD_LABELS: Readonly<Record<string, string>> = {
  card: 'Card',
  us_bank_account: 'ACH Direct Debit',
  bacs_debit: 'Bacs Direct Debit',
  au_becs_debit: 'BECS Direct Debit',
  sepa_debit: 'SEPA Debit',
  bancontact: 'Bancontact',
  customer_balance: 'Bank transfers',
  blik: 'BLIK',
  eps: 'EPS',
  fpx: 'FPX',
  giropay: 'Giropay',
  ideal: 'iDEAL',
  p24: 'Przelewy24',
  sofort: 'SOFORT',
  affirm: 'Affirm',
  afterpay_clearpay: 'Afterpay and Clearpay',
  klarna: 'Klarna',
  cards: 'Cards',
  link: 'Link',
  paynow: 'PayNow',
  promptpay: 'PromptPay',
  boleto: 'Boleto',
  konbini: 'Konbini',
  oxxo: 'OXXO',
  alipay: 'Alipay',
  grabpay: 'GrabPay',
  wechat_pay: 'WeChat Pay',
  google_pay: 'Google Pay',
  apple_pay: 'Apple Pay',
  amazon_pay: 'Amazon Pay',
  acss_debit: 'Canadian pre-authorized debit payment',
  cashapp: 'Cash App',
  mobilepay: 'MobilePay',
  multibanco: 'Multibanco',
  paypal: 'PayPal',
  pix: 'Pix',
  revolut_pay: 'Revolut Pay',
  swish: 'Swish',
  twint: 'TWINT',
  zip: 'Zip',
};

const CARD_WALLET_LABELS: Readonly<Record<string, string>> = {
  amex_express_checkout: 'Amex Express Checkout',
  apple_pay: 'Apple Pay',
  google_pay: 'Google Pay',
  link: 'Link',
  masterpass: 'Masterpass',
  samsung_pay: 'Samsung Pay',
  visa_checkout: 'Visa Checkout',
};

const CARD_BRAND_LABELS: Readonly<Record<string, string>> = {
  amex: 'American Express',
  cartes_bancaires: 'Cartes Bancaires',
  diners: 'Diners Club',
  discover: 'Discover',
  eftpos_au: 'EFTPOS',
  jcb: 'JCB',
  link: 'Link',
  mastercard: 'Mastercard',
  unionpay: 'UnionPay',
  visa: 'Visa',
  unknown: 'Unknown',
};

interface PaymentMethodLabelInput {
  gateway?: string | null;
  method?: string | null;
  brand?: string | null;
  last4?: string | null;
  wallet?: string | null;
  mode?: string | null;
}

export function getPaymentMethodLabel(
  paymentMethod: PaymentMethodLabelInput | null | undefined,
  { includeBrand = true }: { includeBrand?: boolean } = {},
): string {
  if (!paymentMethod?.method) return '';

  const { gateway, method, brand, last4, wallet, mode } = paymentMethod;

  if (gateway === 'manual') return 'Manual';

  let label = STRIPE_PAYMENT_METHOD_LABELS[method] ?? method;

  if (method === 'card' && includeBrand && brand && last4) {
    label = `${CARD_BRAND_LABELS[brand] ?? 'Card'} ending in •••• ${last4}`;
  }

  const walletLabel = method === 'card' && wallet ? CARD_WALLET_LABELS[wallet] : undefined;

  if (walletLabel) {
    return mode === 'express' ? `${label} (${walletLabel} Express)` : `${label} (${walletLabel})`;
  }

  return label;
}

const BANK_METHODS = new Set([
  'us_bank_account',
  'bacs_debit',
  'au_becs_debit',
  'sepa_debit',
  'acss_debit',
  'customer_balance',
]);

export type PaymentMethodKind = 'card' | 'bank' | 'wallet' | 'manual';

export interface PaymentMethodParts {
  kind: PaymentMethodKind;
  name: string;
  last4?: string;
  wallet?: string;
}

/** A payment method broken into parts, for surfaces that word the label themselves. */
export function getPaymentMethodParts(
  paymentMethod: PaymentMethodLabelInput | null | undefined,
): PaymentMethodParts | null {
  if (!paymentMethod?.method && paymentMethod?.gateway !== 'manual') return null;

  const { gateway, method, brand, last4, wallet } = paymentMethod;
  if (gateway === 'manual') return { kind: 'manual', name: '' };

  if (method === 'card') {
    return {
      kind: 'card',
      name: (brand && CARD_BRAND_LABELS[brand]) || STRIPE_PAYMENT_METHOD_LABELS.card,
      last4: last4 ?? undefined,
      wallet: (wallet && CARD_WALLET_LABELS[wallet]) || undefined,
    };
  }

  return {
    kind: BANK_METHODS.has(method as string) ? 'bank' : 'wallet',
    name: STRIPE_PAYMENT_METHOD_LABELS[method as string] ?? (method as string),
    last4: last4 ?? undefined,
  };
}
